<template>
  <div class="deliverability-report relative flex flex-col justify-center items-center">
    <div class="w-full">
      <report-header
        v-if="report"
        :error.sync="error"
        :range="range"
        :report="report"
        @getReport="getReport"
      >
        <template #title>
          campaign deliverability report
        </template>
        <template #subtitle>
          analyze how your campaigns are delivering by carrier
        </template>
      </report-header>
    </div>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else class="w-full mt-12">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        disableTableSort
      >
        <template #campaign_id="item">
          <a :href="`/campaigns/${item.row.campaign_id}`" target="_blank">
            {{item.row[item.column.key]}}
          </a>
        </template>
      </sb-custom-table>
      <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {format, subDays} from 'date-fns';
import { utcToZonedTime } from "date-fns-tz";
import { mapGetters } from "vuex";
import { SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { reportsAPI } from '../../services/api/reportsAPI';
import ReportHeader from "./ReportHeader.vue";

export default {
  name: 'DeliverabilityReport',
  components: {
    SbCustomTable,
    LoadingIcon,
    Pagination,
    ReportHeader
  },
  data() {
    return {
      range: null,
      page: 0,
      report: null,
      tableRows: [],
      loadingReport: false,
      tableColumns: [],
      error: {},
    }
  },
  async created() {
    await this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.range = this.defaultDateRange();
    this.getReport();
  },
  watch: {
    page() {
      this.getReport();
    }
  },
  computed: {
    pagesTotal(){
      return this.report?.pagination?.total_pages || 1;
    },
    ...mapGetters(['merchantTimeZone'])
  },
  methods: {
    defaultDateRange() {
      const currentDate = utcToZonedTime(new Date(), this.merchantTimeZone);
      const minimumDate = subDays(currentDate, 90);

      return {
        min: minimumDate,
        start: minimumDate,
        max: subDays(currentDate, 1),
        end: subDays(currentDate, 1)
      }
    },
    formatReportData() {
      const { cols, results } = this.report.output_data;
      this.tableRows = results;

      if (cols) {
        const columns = Object.keys(cols).map(key => ({
          key,
          value: cols[key].replace(/_/g, '-'),
          width: 80,
        }));

        this.tableColumns = [
          { key: "date", value: "date", width: 180, fixed: true },
          { key: "campaign_id", value: "campaign id", width: 80, fixed: true },
          ...columns.filter((obj) => obj.key !== 'date' && obj.key !== 'campaign_id')
        ];
      }
      else {
        this.error.message = "There is no data yet.";
      }
    },
    async fetchExistingReport() {
      this.loadingReport = true;
      try {
        const { report, pagination } = await reportsAPI.getReportById(this.report.id, this.page + 1);
        this.report = { pagination, ...report };
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    },
    getReport(newReport) {
      if (!this.report?.id || (this.report.id && newReport)) {
        this.initiateReportJob();
      } else {
        this.fetchExistingReport();
      }
    },
    async initiateReportJob() {
      this.loadingReport = true;

      let params = {
        report: {
          type: 'Reports::CampaignDeliverabilityReport'
        }
      }

      if (this.range.start && this.range.end) {
        params = {
          report: {
            ...params.report,
            input_data: {
              date_range_min: format(this.range.start, 'yyyy-MM-dd'),
              date_range_max: format(this.range.end, 'yyyy-MM-dd'),
            }
          }
        }
      }

      const report = await reportsAPI.initializeReportJob(params);

      this.reportId = report.id;
      this.pollReport();
    },
    pollReport() {
      setTimeout(() => {
        reportsAPI.fetchReport(this.reportId)
        .then(({report, pagination}) => {
            if (report.state === 'completed') {
              this.report = {pagination, ...report};
              this.formatReportData();
            } else {
              this.pollReport();
            }
        }).catch(error => {
          this.error.message = "error, please refresh or contact support";
        }).finally(() => {
          this.loadingReport = false;
        });
      }, 1000);
    }
  }
}
</script>

<style lang="scss" scoped>
.deliverability-report {
  &::v-deep {
    table {
      thead {
        tr {
          th {
            vertical-align: bottom;
          }
        }
      }
    }
  }
}
</style>
